import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Trophy, Users, Target, Zap, Download, Crown, Medal,
    Award, Star, Pencil, ChevronDown, ChevronUp, Shield,
    TrendingUp, Calendar, BarChart2, CheckCircle2, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

function TeamBadge({ teamId, teamName, size = 'sm', src }) {
    const [err, setErr] = useState(false);
    const dim = { sm: 'w-7 h-7', md: 'w-9 h-9', lg: 'w-10 h-10', xl: 'w-12 h-12' }[size] || 'w-7 h-7';
    // `src` lets callers pass a CORS-safe data URL (for html2canvas exports).
    const resolved = src || (teamId ? badgeUrl(teamId) : null);
    if (!err && resolved) {
        return (
            <img
                src={resolved}
                alt={teamName}
                className={`${dim} object-contain flex-shrink-0`}
                onError={() => setErr(true)}
            />
        );
    }
    return (
        <div className={`${dim} rounded-full bg-muted flex items-center justify-center flex-shrink-0`}>
            <span className="text-[10px] font-bold">{(teamName || '?')[0]}</span>
        </div>
    );
}

function formatKickoff(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AdminFootballEngagementPage() {
    const { compId } = useParams();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [competitions, setCompetitions] = useState([]);
    const [selectedComp, setSelectedComp] = useState(compId || '');
    const [activeTab, setActiveTab] = useState('matches');
    const [expandedMatches, setExpandedMatches] = useState({});
    const [shareRoundId, setShareRoundId] = useState('');
    const [pickStatus, setPickStatus] = useState(null);
    const [pickRoundId, setPickRoundId] = useState('');
    const [pickLoading, setPickLoading] = useState(false);
    const [statsMatchId, setStatsMatchId] = useState('');
    const [statsApiId, setStatsApiId] = useState('');
    const [statsRefreshing, setStatsRefreshing] = useState(false);
    const [mergeOldId, setMergeOldId] = useState('');
    const [mergeNewId, setMergeNewId] = useState('');
    const [merging, setMerging] = useState(false);
    const [audit, setAudit] = useState(null);
    const [auditing, setAuditing] = useState(false);

    const runAudit = async (allPlayers) => {
        setAuditing(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/audit-picks`,
                { params: allPlayers ? { all_players: true } : {}, headers: authHeaders() }
            );
            setAudit(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Audit failed');
        } finally {
            setAuditing(false);
        }
    };

    const fetchCompetitions = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/football/competitions`, { headers: authHeaders() });
            const comps = res.data.competitions || [];
            setCompetitions(comps);
            if (comps.length > 0) {
                setSelectedComp(prev => prev || comps[0].id);
            }
        } catch {
            toast.error('Failed to load competitions');
        }
    }, []);

    const fetchEngagement = useCallback(async () => {
        if (!selectedComp) return;
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/engagement`,
                { headers: authHeaders() }
            );
            setData(res.data);
        } catch {
            toast.error('Failed to load engagement data');
        } finally {
            setLoading(false);
        }
    }, [selectedComp]);

    useEffect(() => {
        if (!isAuthenticated || !user?.is_admin) {
            navigate('/');
            return;
        }
        fetchCompetitions();
    }, [isAuthenticated, user, navigate, fetchCompetitions]);

    useEffect(() => {
        fetchEngagement();
    }, [fetchEngagement]);

    const fetchPickStatus = useCallback(async (roundId) => {
        if (!selectedComp) return;
        setPickLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/pick-status`,
                { params: roundId ? { round_id: roundId } : {}, headers: authHeaders() }
            );
            setPickStatus(res.data);
            setPickRoundId(res.data.selected_round_id || '');
        } catch {
            toast.error('Failed to load pick status');
        } finally {
            setPickLoading(false);
        }
    }, [selectedComp]);

    // Load pick status when its tab is opened (or the competition changes)
    useEffect(() => {
        if (activeTab === 'picks') fetchPickStatus();
    }, [activeTab, fetchPickStatus]);

    const handleRefreshMatchStats = async () => {
        const id = statsMatchId.trim();
        if (!id) { toast.error('Enter a match ID'); return; }
        setStatsRefreshing(true);
        try {
            const apiId = statsApiId.trim();
            const res = await axios.post(
                `${API_URL}/api/admin/football/match/${id}/refresh-stats`,
                apiId ? { statsapi_match_id: apiId } : {}, { headers: authHeaders() }
            );
            const d = res.data || {};
            if (d.ok) {
                toast.success(`Imported ${d.players_imported} player${d.players_imported !== 1 ? 's' : ''} for match ${id}`);
            } else if (d.errors?.length) {
                toast.error(d.errors.join(' · '));
            } else if (d.players_imported === 0) {
                toast.warning(`No player stats returned for match ${id} — the match may not be final on theStatsAPI yet.`);
            } else {
                toast.success(`Stats refreshed for match ${id}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to refresh match stats');
        } finally {
            setStatsRefreshing(false);
        }
    };

    const handleMergeFixture = async () => {
        const oldId = mergeOldId.trim();
        const newId = mergeNewId.trim();
        if (!oldId || !newId) { toast.error('Enter both the old and new match IDs'); return; }
        if (oldId === newId) { toast.error('Old and new match IDs must be different'); return; }
        if (!window.confirm(
            `Merge fixtures?\n\nAll predictions on match ${oldId} will be moved onto match ${newId}, the new fixture takes over the old one's round, and match ${oldId} is postponed and hidden.\n\nPlayers who already predicted the new fixture keep that pick. This is reversible in the DB but has no undo button — proceed?`
        )) return;
        setMerging(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/merge-fixture`,
                { old_pa_match_id: oldId, new_pa_match_id: newId }, { headers: authHeaders() }
            );
            const d = res.data || {};
            toast.success(
                `Moved ${d.predictions_moved} prediction${d.predictions_moved !== 1 ? 's' : ''} to ${newId}` +
                (d.conflicts_kept ? ` · ${d.conflicts_kept} kept their existing new-fixture pick` : '') +
                (d.round_copied ? '' : ' · ⚠ old fixture had no round to copy')
            );
            setMergeOldId(''); setMergeNewId('');
            fetchEngagement();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to merge fixtures');
        } finally {
            setMerging(false);
        }
    };

    const handleRollbackMerge = async () => {
        const oldId = mergeOldId.trim();
        const newId = mergeNewId.trim();
        if (!oldId || !newId) { toast.error('Enter the same old and new match IDs used for the merge'); return; }
        if (!window.confirm(
            `Roll back the merge of ${oldId} → ${newId}?\n\nPredictions the merge moved will be returned to ${oldId}, and both fixtures' round/status restored. Any predictions made on the new fixture AFTER the merge are left in place.`
        )) return;
        setMerging(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/merge-fixture/rollback`,
                { old_pa_match_id: oldId, new_pa_match_id: newId }, { headers: authHeaders() }
            );
            const d = res.data || {};
            toast.success(
                `Restored ${d.predictions_restored} prediction${d.predictions_restored !== 1 ? 's' : ''} to ${oldId}` +
                (d.extra_on_new_kept ? ` · ${d.extra_on_new_kept} post-merge pick(s) left on the new fixture` : '')
            );
            fetchEngagement();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to roll back merge');
        } finally {
            setMerging(false);
        }
    };

    const handleToggleEliminated = async (userId, eliminated, username) => {
        if (eliminated && !window.confirm(
            `Mark ${username || 'this player'} as out?\n\nThey'll drop to the bottom of the table and stop getting pick-reminder emails. Their points and history are kept, and you can reinstate them later.`
        )) return;
        try {
            await axios.post(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/entries/${userId}/eliminate`,
                { eliminated }, { headers: authHeaders() }
            );
            toast.success(eliminated ? `${username || 'Player'} marked out` : `${username || 'Player'} reinstated`);
            fetchPickStatus(pickRoundId);
            fetchEngagement();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update player');
        }
    };

    // Default the weekly share card to the active round (or the first one)
    useEffect(() => {
        const rounds = data?.rounds || [];
        if (rounds.length && !rounds.some(r => r.id === shareRoundId)) {
            const active = rounds.find(r => r.status === 'active');
            setShareRoundId((active || rounds[0]).id);
        }
    }, [data, shareRoundId]);

    const toggleMatch = (mid) => setExpandedMatches(prev => ({ ...prev, [mid]: !prev[mid] }));

    const handleSetFt = async (paMatchId, homeScore, awayScore) => {
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/football/competitions/${selectedComp}/matches/${paMatchId}/set-ft-score`,
                { home_score: homeScore, away_score: awayScore },
                { headers: authHeaders() }
            );
            toast.success(`Full-time score set ${homeScore}–${awayScore} · ${res.data.settled} prediction${res.data.settled !== 1 ? 's' : ''} settled`);
            fetchEngagement();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to set full-time score');
        }
    };

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-background p-4 md:p-8">
                <div className="max-w-7xl mx-auto space-y-6">
                    <Skeleton className="h-12 w-64" />
                    <Skeleton className="h-[400px] w-full" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-border">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-2 page-title">
                                <BarChart2 className="h-6 w-6 text-[#00D4AA]" />
                                Football Engagement
                            </h1>
                            <p className="text-slate-400 mt-1">Pick distributions, leaderboard & tournament summary</p>
                        </div>
                        <Select value={selectedComp} onValueChange={setSelectedComp}>
                            <SelectTrigger className="w-[220px] bg-slate-800 border-slate-700">
                                <SelectValue placeholder="Select competition" />
                            </SelectTrigger>
                            <SelectContent>
                                {competitions.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-6">
                {data && (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                        <TabsList className="bg-slate-800">
                            <TabsTrigger value="matches" className="gap-2">
                                <BarChart2 className="h-4 w-4" />
                                Match Picks
                            </TabsTrigger>
                            <TabsTrigger value="leaderboard" className="gap-2">
                                <Trophy className="h-4 w-4" />
                                Leaderboard
                            </TabsTrigger>
                            <TabsTrigger value="picks" className="gap-2">
                                <XCircle className="h-4 w-4" />
                                Pick Status
                            </TabsTrigger>
                            <TabsTrigger value="rounds" className="gap-2">
                                <Calendar className="h-4 w-4" />
                                Rounds
                            </TabsTrigger>
                            <TabsTrigger value="summary" className="gap-2">
                                <Award className="h-4 w-4" />
                                Summary Card
                            </TabsTrigger>
                        </TabsList>

                        {/* Stats bar */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <StatCard icon={<Users className="h-5 w-5 text-primary" />} iconBg="bg-primary/10"
                                value={data.total_entries} label="Players" />
                            <StatCard icon={<Target className="h-5 w-5 text-amber-500" />} iconBg="bg-amber-500/10"
                                value={data.total_predictions} label="Predictions" />
                            <StatCard icon={<CheckCircle2 className="h-5 w-5 text-green-500" />} iconBg="bg-green-500/10"
                                value={data.total_exact_scores} label="Exact Scores" />
                            <StatCard icon={<Zap className="h-5 w-5 text-purple-500" />} iconBg="bg-purple-500/10"
                                value={data.total_correct_results} label="Correct Results" />
                        </div>

                        {/* Match Picks Tab */}
                        <TabsContent value="matches" className="mt-0 space-y-4">
                            <Card className="bg-slate-900 border-slate-700">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-white text-base flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4 text-[#00D4AA]" />
                                        Re-import match stats
                                    </CardTitle>
                                    <CardDescription>
                                        Backfill a match's stats from theStatsAPI (e.g. after a lapsed subscription).
                                        Paste the match ID from its URL — <span className="font-mono">/wc2026/match/<span className="text-[#00D4AA]">4560663</span></span>.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <input
                                            value={statsMatchId}
                                            onChange={(e) => setStatsMatchId(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleRefreshMatchStats(); }}
                                            placeholder="PA match ID e.g. 4560663"
                                            className="w-[220px] rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-[#00D4AA]/50"
                                        />
                                        <input
                                            value={statsApiId}
                                            onChange={(e) => setStatsApiId(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleRefreshMatchStats(); }}
                                            placeholder="StatsAPI ID (optional) e.g. mt_362533007"
                                            className="w-[280px] rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-[#00D4AA]/50"
                                        />
                                        <Button onClick={handleRefreshMatchStats} disabled={statsRefreshing} className="gap-2">
                                            <TrendingUp className="h-4 w-4" />
                                            {statsRefreshing ? 'Importing…' : 'Re-import stats'}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Leave the StatsAPI ID blank to auto-resolve. Fill it in (from theStatsAPI) to pin the match
                                        directly when auto-resolution can't find it.
                                    </p>
                                </CardContent>
                            </Card>

                            <Card className="bg-slate-900 border-slate-700">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-white text-base flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-amber-400" />
                                        Merge rescheduled fixture
                                    </CardTitle>
                                    <CardDescription>
                                        When a game is postponed and PA issues a new fixture, move all existing predictions onto it.
                                        Sync the competition first so the new fixture exists, then enter both match IDs.
                                        The old fixture is postponed and hidden; players who already picked the new one keep that pick.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <input
                                            value={mergeOldId}
                                            onChange={(e) => setMergeOldId(e.target.value)}
                                            placeholder="Old (postponed) match ID"
                                            className="w-[240px] rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-amber-400/50"
                                        />
                                        <span className="text-muted-foreground text-sm">→</span>
                                        <input
                                            value={mergeNewId}
                                            onChange={(e) => setMergeNewId(e.target.value)}
                                            placeholder="New (rescheduled) match ID"
                                            className="w-[240px] rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white font-mono outline-none focus:border-amber-400/50"
                                        />
                                        <Button
                                            onClick={handleMergeFixture}
                                            disabled={merging}
                                            className="gap-2 bg-amber-600 hover:bg-amber-500 text-white"
                                        >
                                            <Calendar className="h-4 w-4" />
                                            {merging ? 'Merging…' : 'Merge predictions'}
                                        </Button>
                                        <Button
                                            onClick={handleRollbackMerge}
                                            disabled={merging}
                                            variant="outline"
                                            className="gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                                        >
                                            <ChevronUp className="h-4 w-4" />
                                            Roll back
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Roll back undoes a merge for the same two IDs — predictions the merge moved go back to the
                                        old fixture and both fixtures are restored. Picks made on the new fixture after the merge stay put.
                                    </p>
                                </CardContent>
                            </Card>
                            {data.match_distributions?.length === 0 && (
                                <p className="text-muted-foreground text-center py-8">No revealed matches yet.</p>
                            )}
                            {data.match_distributions?.map(m => (
                                <MatchPickCard
                                    key={m.pa_match_id}
                                    match={m}
                                    expanded={expandedMatches[m.pa_match_id]}
                                    onToggle={() => toggleMatch(m.pa_match_id)}
                                    onSetFt={handleSetFt}
                                />
                            ))}
                        </TabsContent>

                        {/* Leaderboard Tab */}
                        <TabsContent value="leaderboard" className="mt-0">
                            <LeaderboardTable leaderboard={data.leaderboard} />
                        </TabsContent>

                        {/* Pick Status Tab */}
                        <TabsContent value="picks" className="mt-0 space-y-4">
                            <Card className="bg-slate-900 border-slate-700">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-white text-base flex items-center gap-2">
                                        <Shield className="h-4 w-4 text-[#00D4AA]" />
                                        Pick Integrity Audit
                                    </CardTitle>
                                    <CardDescription>
                                        Verifies every pick was submitted before the 30-minute lock window and that every
                                        awarded score recomputes exactly (base + bonus + closest-wins). Defaults to the
                                        active (non-eliminated) players — e.g. your two finalists.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <Button onClick={() => runAudit(false)} disabled={auditing} className="gap-2">
                                            <Shield className="h-4 w-4" />
                                            {auditing ? 'Auditing…' : 'Audit active players'}
                                        </Button>
                                        <Button onClick={() => runAudit(true)} disabled={auditing} variant="outline"
                                            className="border-slate-600 text-slate-300 hover:bg-slate-800">
                                            Audit everyone
                                        </Button>
                                    </div>
                                    {audit && (
                                        <div className="space-y-3">
                                            <div className={`rounded-md px-3 py-2 text-sm font-semibold border ${
                                                audit.flags?.length
                                                    ? 'bg-red-500/10 border-red-500/40 text-red-400'
                                                    : 'bg-green-500/10 border-green-500/40 text-green-400'
                                            }`}>
                                                {audit.verdict} <span className="font-normal text-muted-foreground">· scope: {audit.scope}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                {audit.audited_players?.map(p => (
                                                    <div key={p.user_id} className="rounded-md bg-slate-800 border border-slate-700 px-3 py-2 text-xs text-slate-300">
                                                        <span className="font-semibold text-white">{p.username}</span>
                                                        {' — '}{p.picks} picks · <span className="text-green-400">{p.clean} clean</span>
                                                        {p.flagged > 0 && <> · <span className="text-red-400">{p.flagged} flagged</span></>}
                                                    </div>
                                                ))}
                                            </div>
                                            {audit.flags?.length > 0 && (
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow className="border-slate-800">
                                                            <TableHead className="text-slate-400">Severity</TableHead>
                                                            <TableHead className="text-slate-400">Player</TableHead>
                                                            <TableHead className="text-slate-400">Match</TableHead>
                                                            <TableHead className="text-slate-400">Detail</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {audit.flags.map((f, i) => (
                                                            <TableRow key={i} className="border-slate-800">
                                                                <TableCell>
                                                                    <Badge variant="outline" className={f.severity === 'CRITICAL'
                                                                        ? 'border-red-500/50 text-red-400'
                                                                        : 'border-amber-500/50 text-amber-400'}>
                                                                        {f.severity}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-white font-medium">{f.username}</TableCell>
                                                                <TableCell className="text-slate-300">{f.match}</TableCell>
                                                                <TableCell className="text-slate-300 text-xs">{f.detail}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            <PickStatusTab
                                data={pickStatus}
                                loading={pickLoading}
                                roundId={pickRoundId}
                                onRoundChange={(rid) => { setPickRoundId(rid); fetchPickStatus(rid); }}
                                onToggle={handleToggleEliminated}
                            />
                        </TabsContent>

                        {/* Rounds Tab */}
                        <TabsContent value="rounds" className="mt-0 space-y-4">
                            {data.rounds?.length === 0 && (
                                <p className="text-muted-foreground text-center py-8">No rounds found.</p>
                            )}
                            {data.rounds?.length > 0 && (
                                <Card className="bg-slate-900 border-slate-700">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-white text-base flex items-center gap-2">
                                            <Award className="h-4 w-4 text-[#00D4AA]" />
                                            Weekly Share Card
                                        </CardTitle>
                                        <CardDescription>Pick a week, then export a square image for socials.</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <RoundShareCard
                                            rounds={data.rounds}
                                            comp={data.competition}
                                            selectedId={shareRoundId}
                                            onSelect={setShareRoundId}
                                        />
                                    </CardContent>
                                </Card>
                            )}
                            {data.rounds?.map(r => (
                                <RoundCard key={r.id} round={r} />
                            ))}
                        </TabsContent>

                        {/* Summary Card Tab */}
                        <TabsContent value="summary" className="mt-0">
                            <TournamentSummaryCard data={data} />
                        </TabsContent>
                    </Tabs>
                )}
            </div>
        </div>
    );
}

function StatCard({ icon, iconBg, value, label }) {
    return (
        <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${iconBg}`}>{icon}</div>
                    <div>
                        <p className="text-2xl font-bold">{value ?? '–'}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function MatchPickCard({ match, expanded, onToggle, onSetFt }) {
    const isFinished = match.status === 'finished';
    const isLive = match.status === 'live' || match.status === 'in progress';
    const isUpcoming = !isFinished && !isLive;
    const topPick = match.distribution?.[0];

    const [ftH, setFtH] = useState(match.home_score ?? '');
    const [ftA, setFtA] = useState(match.away_score ?? '');
    const [savingFt, setSavingFt] = useState(false);
    const submitFt = async () => {
        if (ftH === '' || ftA === '' || !onSetFt) return;
        setSavingFt(true);
        try { await onSetFt(match.pa_match_id, parseInt(ftH, 10), parseInt(ftA, 10)); }
        finally { setSavingFt(false); }
    };

    return (
        <Card className="bg-slate-900 border-slate-700 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-800/50 px-5 py-4 border-b border-slate-700/50">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} />
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-white text-sm">{match.home_team_name}</span>
                                <span className="text-slate-400 text-sm">vs</span>
                                <span className="font-bold text-white text-sm">{match.away_team_name}</span>
                                <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} />
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {match.round_name && (
                                    <span className="text-xs text-[#00D4AA]">{match.round_name}</span>
                                )}
                                <span className="text-xs text-slate-500">{formatKickoff(match.kickoff_time)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {isFinished && match.home_score !== null && (
                            <div className="text-center">
                                <span className="text-xl font-black text-white tabular-nums">
                                    {match.home_score}–{match.away_score}
                                </span>
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Result</p>
                            </div>
                        )}
                        <Badge className={
                            isFinished ? 'bg-green-500/20 text-green-400 border-green-500/30'
                            : isLive    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            :             'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        }>
                            {match.total_pickers} picks
                        </Badge>
                        {isUpcoming && (
                            <span className="text-xs text-slate-400 font-medium">Upcoming</span>
                        )}
                        {isLive && (
                            <span className="text-xs text-red-400 font-bold animate-pulse">LIVE</span>
                        )}
                    </div>
                </div>
            </div>

            <CardContent className="p-5">
                {/* Admin: manually set the 90-min full-time score (for KO ties whose
                    FT score wasn't captured before extra time) */}
                <div className="flex items-center gap-2 mb-4 text-xs flex-wrap bg-slate-800/40 rounded-lg px-3 py-2">
                    <span className="text-slate-400 font-semibold uppercase tracking-wide">Set 90′ full-time</span>
                    <input type="number" min="0" value={ftH} onChange={e => setFtH(e.target.value)}
                        className="w-12 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-center" />
                    <span className="text-slate-500">–</span>
                    <input type="number" min="0" value={ftA} onChange={e => setFtA(e.target.value)}
                        className="w-12 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-center" />
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={submitFt} disabled={savingFt}>
                        <CheckCircle2 className="h-3 w-3" />
                        {savingFt ? 'Saving…' : 'Save & settle'}
                    </Button>
                    {match.ft_home_score != null && (
                        <span className="text-[#00D4AA]">FT locked: {match.ft_home_score}–{match.ft_away_score}</span>
                    )}
                </div>

                {/* Top 3 score predictions podium */}
                <div className="flex justify-center gap-6 mb-5 flex-wrap">
                    {[1, 0, 2].map((pickIdx, displayIdx) => {
                        const pick = match.distribution?.[pickIdx];
                        if (!pick) return null;
                        const labels = ['2ND', '1ST', '3RD'];
                        const isCenter = displayIdx === 1;
                        return (
                            <div key={pickIdx} className={`text-center ${isCenter ? 'scale-105' : ''}`}>
                                <p className={`text-[10px] font-bold mb-2 tracking-wider ${isCenter ? 'text-[#00D4AA]' : 'text-slate-400'}`}>
                                    {labels[displayIdx]} PICK
                                </p>
                                <div className={`mx-auto mb-2 rounded-xl px-4 py-2 font-black tabular-nums text-lg border-2 ${
                                    pick.correct
                                        ? 'bg-green-500/20 border-green-500 text-green-400'
                                        : isCenter
                                            ? 'bg-[#00D4AA]/10 border-[#00D4AA] text-[#00D4AA]'
                                            : 'bg-slate-800 border-slate-600 text-white'
                                }`}>
                                    {pick.score}
                                </div>
                                {pick.correct && (
                                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] mb-1">
                                        Exact Score!
                                    </Badge>
                                )}
                                {!pick.correct && pick.correct_result && (
                                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] mb-1">
                                        Correct Result
                                    </Badge>
                                )}
                                <p className={`text-xs ${isCenter ? 'text-[#00D4AA]' : 'text-slate-400'}`}>
                                    <span className="font-bold">{pick.count}</span> ({pick.percentage}%)
                                </p>
                            </div>
                        );
                    })}
                </div>

                <Collapsible open={expanded} onOpenChange={onToggle}>
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" className="w-full justify-between text-slate-400 hover:text-white text-xs">
                            <span className="font-semibold uppercase tracking-wider">Full Breakdown</span>
                            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="mt-3 space-y-2">
                            {match.distribution?.map((d, idx) => (
                                <div key={d.score} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                                    d.correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-slate-800/50'
                                }`}>
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                        idx < 3 ? 'bg-primary/20 text-primary' : 'bg-slate-700 text-slate-400'
                                    }`}>{idx + 1}</span>
                                    <span className={`font-black tabular-nums w-10 text-sm ${d.correct ? 'text-green-400' : 'text-white'}`}>
                                        {d.score}
                                    </span>
                                    <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${d.correct ? 'bg-green-500' : 'bg-primary'}`}
                                            style={{ width: `${Math.max(d.percentage, 2)}%` }}
                                        />
                                    </div>
                                    <span className="text-xs text-slate-400 w-16 text-right tabular-nums">
                                        {d.count} ({d.percentage}%)
                                    </span>
                                    {d.correct && <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />}
                                    {!d.correct && d.correct_result && (
                                        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[9px]">Result</Badge>
                                    )}
                                </div>
                            ))}
                            {/* Who picked what — with submission time for cheat auditing */}
                            {match.distribution?.some(d => d.pickers?.length > 0) && (() => {
                                // Flatten across scorelines and sort chronologically so the
                                // whole pick timeline reads top-to-bottom by submission time.
                                const rows = match.distribution.flatMap(d =>
                                    (d.pickers || []).map(pk => {
                                        const obj = typeof pk === 'string' ? { name: pk } : pk;
                                        return { ...obj, score: d.score, correct: d.correct, correct_result: d.correct_result };
                                    })
                                ).sort((a, b) => (a.at || '').localeCompare(b.at || ''));
                                const lateCount = rows.filter(r => r.late).length;
                                return (
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Pickers <span className="text-slate-500">· when submitted</span></p>
                                            {lateCount > 0 && (
                                                <Badge className="bg-red-500/15 text-red-400 border-red-500/30 text-[10px]">
                                                    {lateCount} after kickoff
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {rows.map((p, i) => (
                                                <div key={i} className={`flex items-center justify-between rounded px-3 py-1.5 ${p.late ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800'}`}>
                                                    <div className="min-w-0">
                                                        <span className="text-sm text-white truncate">{p.name}</span>
                                                        {p.at && (
                                                            <span className={`block text-[10px] tabular-nums ${p.late ? 'text-red-400 font-semibold' : 'text-slate-500'}`}>
                                                                {formatKickoff(p.at)}{p.late ? ' · LATE' : ''}{p.locked ? '' : ' · unlocked'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className={`text-xs font-bold tabular-nums flex-shrink-0 ${p.correct ? 'text-green-400' : p.correct_result ? 'text-blue-400' : 'text-slate-500'}`}>
                                                        {p.score}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </CardContent>
        </Card>
    );
}

function PickStatusTab({ data, loading, roundId, onRoundChange, onToggle }) {
    if (loading && !data) return <Skeleton className="h-64 w-full bg-slate-800" />;
    if (!data) return null;

    const rounds = data.rounds || [];
    const players = data.players || [];
    const currentId = roundId || data.selected_round_id || '';
    const selected = rounds.find(r => r.round_id === currentId);
    const noPickActive = players.filter(p => !p.eliminated && !p.made_any);

    const roundLabel = (r) => {
        const base = r.name || `Round ${r.round_number}`;
        const tag = r.status === 'active' ? ' · active'
            : r.status === 'complete' ? ' · complete'
            : r.status === 'upcoming' ? ' · upcoming' : '';
        return base + tag;
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={currentId} onValueChange={onRoundChange}>
                    <SelectTrigger className="w-[260px] bg-slate-800 border-slate-700 text-white">
                        <SelectValue placeholder="Select a round" />
                    </SelectTrigger>
                    <SelectContent>
                        {rounds.map(r => (
                            <SelectItem key={r.round_id} value={r.round_id}>{roundLabel(r)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {selected && (
                    <span className="text-sm text-muted-foreground">
                        {selected.match_count} match{selected.match_count !== 1 ? 'es' : ''} ·{' '}
                        <span className={noPickActive.length ? 'text-red-400 font-semibold' : 'text-green-400 font-medium'}>
                            {noPickActive.length}
                        </span>{' '}
                        active player{noPickActive.length !== 1 ? 's' : ''} with no picks
                    </span>
                )}
            </div>

            {players.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No players to show for this round.</p>
            ) : (
                <Card className="bg-slate-900 border-slate-700">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-800">
                                    <TableHead className="text-slate-400">Player</TableHead>
                                    <TableHead className="text-slate-400 text-center">Picks</TableHead>
                                    <TableHead className="text-slate-400 text-center">Status</TableHead>
                                    <TableHead className="text-slate-400 text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {players.map(p => {
                                    const noPicks = !p.made_any;
                                    return (
                                        <TableRow key={p.user_id} className={`border-slate-800 ${p.eliminated ? 'opacity-50' : ''}`}>
                                            <TableCell className="font-medium text-white">{p.username}</TableCell>
                                            <TableCell className="text-center">
                                                <span className={noPicks && !p.eliminated ? 'text-red-400 font-semibold' : 'text-slate-300'}>
                                                    {p.picks_made}/{p.total_matches}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {p.eliminated ? (
                                                    <Badge variant="outline" className="border-red-500/40 text-red-400">Out</Badge>
                                                ) : noPicks ? (
                                                    <Badge variant="outline" className="border-amber-500/40 text-amber-400">No picks</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="border-green-500/40 text-green-400">Active</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {p.eliminated ? (
                                                    p.manual_eliminated ? (
                                                        <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white"
                                                            onClick={() => onToggle(p.user_id, false, p.username)}>
                                                            Reinstate
                                                        </Button>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">knocked out</span>
                                                    )
                                                ) : (
                                                    <Button size="sm" variant="outline"
                                                        className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                                                        onClick={() => onToggle(p.user_id, true, p.username)}>
                                                        Mark out
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

function LeaderboardTable({ leaderboard }) {
    // Elimination cut-off: players ranked BELOW this line are flagged for relegation.
    const [cutoff, setCutoff] = useState('');

    if (!leaderboard?.length) return <p className="text-muted-foreground text-center py-8">No leaderboard data.</p>;

    const getRankStyle = (rank) => {
        if (rank === 1) return 'bg-amber-500 text-black';
        if (rank === 2) return 'bg-gray-400 text-black';
        if (rank === 3) return 'bg-amber-700 text-white';
        return 'bg-slate-700 text-slate-300';
    };

    const total = leaderboard.length;
    const cutNum = parseInt(cutoff, 10);
    const cutActive = Number.isFinite(cutNum) && cutNum > 0 && cutNum < total;
    const relegated = cutActive ? leaderboard.filter(r => r.rank > cutNum).length : 0;
    const relegatedPct = total ? Math.round((relegated / total) * 100) : 0;
    // Survival line for relegating the bottom third (≈ World Cup group-stage attrition)
    const bottom33Line = Math.max(1, total - Math.round(total * 0.33));

    return (
        <Card className="bg-slate-900 border-slate-700">
            {/* Elimination cut-off control */}
            <div className="flex flex-wrap items-center gap-3 p-3 border-b border-slate-700">
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Relegation line · keep top</span>
                    <input
                        type="number"
                        min="1"
                        max={total}
                        value={cutoff}
                        onChange={e => setCutoff(e.target.value)}
                        placeholder={`e.g. ${bottom33Line}`}
                        className="w-24 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white"
                    />
                    <span className="text-xs text-slate-500">of {total}</span>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setCutoff(String(bottom33Line))}>
                    Bottom 33%
                </Button>
                {cutActive && (
                    <span className="text-xs text-red-400">
                        {relegated} relegated ({relegatedPct}%) · {cutNum} survive
                    </span>
                )}
                {cutoff && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-400" onClick={() => setCutoff('')}>
                        Clear
                    </Button>
                )}
            </div>
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow className="border-slate-700 bg-slate-800/50">
                            <TableHead className="text-slate-400 w-12">#</TableHead>
                            <TableHead className="text-slate-400">Player</TableHead>
                            <TableHead className="text-slate-400 text-center">Points</TableHead>
                            <TableHead className="text-slate-400 text-center">Exact</TableHead>
                            <TableHead className="text-slate-400 text-center">Correct</TableHead>
                            <TableHead className="text-slate-400 text-center">Picks</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {leaderboard.map((row) => {
                            const willRelegate = cutActive && row.rank > cutNum;
                            return (
                            <TableRow key={row.user_id} className={`border-slate-800 ${row.eliminated ? 'opacity-50' : ''} ${willRelegate ? 'bg-red-500/10 border-l-2 border-l-red-500/50' : ''}`}>
                                <TableCell>
                                    <Badge className={`${getRankStyle(row.rank)} w-7 h-7 p-0 justify-center rounded-full`}>
                                        {row.eliminated ? <Shield className="h-3 w-3" /> : row.rank}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        {row.rank === 1 && !row.eliminated && <Crown className="h-4 w-4 text-amber-500 shrink-0" />}
                                        {row.rank === 2 && !row.eliminated && <Medal className="h-4 w-4 text-gray-400 shrink-0" />}
                                        {row.rank === 3 && !row.eliminated && <Medal className="h-4 w-4 text-amber-700 shrink-0" />}
                                        <span className="font-medium">{row.username}</span>
                                        {row.eliminated && (
                                            <Badge variant="outline" className="text-destructive border-destructive/30 text-[10px]">OUT</Badge>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="text-center">
                                    <span className="text-lg font-bold text-primary">{row.total_points}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                    <span className="text-green-400 font-semibold">{row.exact_scores}</span>
                                </TableCell>
                                <TableCell className="text-center">
                                    <span className="text-blue-400">{row.correct_results}</span>
                                </TableCell>
                                <TableCell className="text-center text-slate-400">{row.picks}</TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function RoundCard({ round }) {
    const statusColors = {
        active: 'bg-green-500/20 text-green-400 border-green-500/30',
        complete: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
        upcoming: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    };

    return (
        <Card className="bg-slate-900 border-slate-700">
            <CardHeader className="border-b border-slate-700 pb-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-[#00D4AA]" />
                            {round.name}
                        </CardTitle>
                        <CardDescription>
                            {round.match_count} matches · {round.total_predictions} predictions
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <Badge className={statusColors[round.status] || 'bg-slate-500/20 text-slate-400'}>
                            {round.status}
                        </Badge>
                        <div className="flex gap-4">
                            <div className="text-center">
                                <p className="text-lg font-bold text-green-400">{round.exact_scores}</p>
                                <p className="text-[10px] text-slate-400">Exact Scores</p>
                            </div>
                            <div className="text-center">
                                <p className="text-lg font-bold text-blue-400">{round.correct_results}</p>
                                <p className="text-[10px] text-slate-400">Correct Results</p>
                            </div>
                        </div>
                    </div>
                </div>
            </CardHeader>
            {round.leaderboard?.length > 0 && (
                <CardContent className="p-4">
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-3">Round Leaderboard</p>
                    <div className="space-y-2">
                        {round.leaderboard.slice(0, 5).map(row => (
                            <div key={row.user_id} className="flex items-center gap-3 bg-slate-800/50 rounded-lg px-3 py-2">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                    row.rank === 1 ? 'bg-amber-500 text-black' :
                                    row.rank === 2 ? 'bg-gray-400 text-black' :
                                    row.rank === 3 ? 'bg-amber-700 text-white' :
                                    'bg-slate-700 text-slate-400'
                                }`}>{row.rank}</span>
                                <span className="flex-1 text-sm text-white font-medium">{row.username}</span>
                                <span className="text-primary font-bold tabular-nums">{row.points} pts</span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

// Compact vertical "stat tablet" for the weekly card — leads with the big
// number/score, then a one-line context. Sits in a row of 3. Badge facts
// show small crests above the value.
function FactTile({ fact, editableProps, badgeData = {} }) {
    const hasBadges = fact.home_team_id || fact.away_team_id;

    // big = the headline stat (score/%/count); valTone its colour; ctx = context line
    let big = null, valTone = 'text-[#00D4AA]', ctx = null;
    switch (fact.type) {
        case 'winning_margin':
            big = fact.gap > 0 ? `+${fact.gap}` : `${fact.points}`;
            valTone = 'text-[#00D4AA]';
            ctx = <>{fact.user_name} · {fact.gap > 0 ? 'pts clear' : 'pts, joint top'}</>;
            break;
        case 'most_exact_scores':
            big = `${fact.count}`;
            valTone = 'text-green-400';
            ctx = <>{fact.user_name} · exact scores</>;
            break;
        case 'most_correct_results':
            big = `${fact.count}/${fact.total_picks}`;
            valTone = 'text-blue-400';
            ctx = <>{fact.user_name} · results right</>;
            break;
        case 'highest_scoring_pred':
            big = `+${fact.points}`;
            valTone = 'text-amber-400';
            ctx = <>{fact.user_name} · {fact.predicted_score}</>;
            break;
        case 'lone_wolf':
            big = fact.score;
            valTone = 'text-[#00D4AA]';
            ctx = <>only {fact.user_name}</>;
            break;
        case 'goal_fest':
            big = fact.score;
            valTone = 'text-amber-400';
            ctx = <>{fact.goals} goals</>;
            break;
        case 'trickiest':
            big = `${fact.percentage}%`;
            valTone = 'text-red-400';
            ctx = <>got the {fact.score}</>;
            break;
        case 'peoples_pick':
            big = fact.score;
            valTone = 'text-[#00D4AA]';
            ctx = <>picked {fact.count}× this week</>;
            break;
        case 'popular_correct':
            big = fact.score;
            valTone = 'text-green-400';
            ctx = <>{fact.count} called it ({fact.percentage}%)</>;
            break;
        case 'no_one_correct':
            big = fact.actual_score;
            valTone = 'text-red-400';
            ctx = <>0 of {fact.total_pickers} saw it</>;
            break;
        default:
            return null;
    }

    return (
        <div className="bg-gradient-to-br from-[#0f1d35] to-[#0a1628] rounded-xl px-3 py-4 border border-[#1e3a5f]/50 flex flex-col items-center text-center">
            <p className="text-[10px] text-[#00D4AA] font-bold tracking-wide leading-normal mb-2 whitespace-nowrap">{fact.title}</p>
            {hasBadges ? (
                <div className="flex items-center gap-1 mb-1.5">
                    <TeamBadge teamId={fact.home_team_id} teamName={fact.match} size="sm" src={badgeData[fact.home_team_id]} />
                    <span className="text-slate-500 text-[10px] font-bold">v</span>
                    <TeamBadge teamId={fact.away_team_id} teamName={fact.match} size="sm" src={badgeData[fact.away_team_id]} />
                </div>
            ) : (
                <div className="h-7 flex items-center justify-center mb-1.5">
                    <span className="text-2xl">{(fact.title.match(/\p{Emoji}/u) || [''])[0]}</span>
                </div>
            )}
            <p className={`font-black tabular-nums leading-snug ${valTone} text-2xl`}>{big}</p>
            <p className="text-slate-300 text-[11px] leading-snug mt-1.5" {...editableProps}>{ctx}</p>
        </div>
    );
}

// Pick up to `max` facts that are varied: distinct types AND distinct matches,
// in a priority order that favours the punchiest, most shareable stats.
function selectVariedFacts(funFacts, max = 3) {
    const priority = [
        'lone_wolf', 'goal_fest', 'trickiest', 'popular_correct',
        'no_one_correct', 'peoples_pick',
        'most_exact_scores', 'winning_margin', 'most_correct_results',
    ];
    const sorted = [...(funFacts || [])].sort(
        (a, b) => priority.indexOf(a.type) - priority.indexOf(b.type)
    );
    const chosen = [];
    const usedMatches = new Set();
    for (const f of sorted) {
        if (chosen.length >= max) break;
        if (f.match && usedMatches.has(f.match)) continue; // no two facts on the same game
        chosen.push(f);
        if (f.match) usedMatches.add(f.match);
    }
    return chosen;
}

// Square (1:1) shareable card for a single week/round.
function RoundShareCard({ rounds, comp, selectedId, onSelect }) {
    const cardRef = useRef(null);
    const [editMode, setEditMode] = useState(false);
    const [badgeData, setBadgeData] = useState({}); // teamId → CORS-safe data URL

    const round = rounds.find(r => r.id === selectedId) || rounds[0];

    // Badges live on an S3 bucket with no CORS header, so html2canvas can't
    // capture them directly. Proxy each through the backend (returns a base64
    // data URL) so the exported PNG includes the crests.
    useEffect(() => {
        const f = selectVariedFacts(round?.fun_facts, 3);
        const ids = [...new Set(f.flatMap(x => [x.home_team_id, x.away_team_id]).filter(Boolean))];
        const missing = ids.filter(id => !(id in badgeData));
        if (!missing.length) return;
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(missing.map(async id => {
                try {
                    const res = await axios.get(`${API_URL}/api/proxy/image`, { params: { url: badgeUrl(id) } });
                    return [id, res.data?.data || null];
                } catch { return [id, null]; }
            }));
            if (!cancelled) setBadgeData(prev => ({ ...prev, ...Object.fromEntries(entries) }));
        })();
        return () => { cancelled = true; };
    }, [round, badgeData]);

    const handleExport = async () => {
        if (!cardRef.current) return;
        setEditMode(false);
        await new Promise(r => setTimeout(r, 120));
        try {
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: '#0a1628',
                scale: 2,
                useCORS: true,
            });
            const link = document.createElement('a');
            const safe = (round?.name || 'week').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
            link.download = `${comp?.name || 'football'}-${safe}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            toast.success('Weekly card exported!');
        } catch {
            toast.error('Failed to export image');
        }
    };

    const editableProps = editMode ? {
        contentEditable: true,
        suppressContentEditableWarning: true,
        style: { outline: '1px dashed rgba(0,212,170,0.5)', borderRadius: '4px', padding: '1px 3px', cursor: 'text' },
    } : {};

    if (!round) return <p className="text-muted-foreground text-center py-8">No rounds to share yet.</p>;

    const top3 = round.top3 || [];
    const facts = selectVariedFacts(round.fun_facts, 3);

    const rankStyles = [
        'bg-amber-500 text-black',   // 1st
        'bg-slate-400 text-black',   // 2nd
        'bg-amber-700 text-white',   // 3rd
    ];

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <Select value={round.id} onValueChange={onSelect}>
                    <SelectTrigger className="w-full sm:w-[240px] bg-slate-800 border-slate-700">
                        <SelectValue placeholder="Select week" />
                    </SelectTrigger>
                    <SelectContent>
                        {rounds.map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="flex gap-2">
                    <Button variant={editMode ? 'default' : 'outline'} onClick={() => setEditMode(!editMode)} className="gap-2">
                        <Pencil className="h-4 w-4" />
                        {editMode ? 'Editing' : 'Edit Text'}
                    </Button>
                    <Button variant="outline" onClick={handleExport} className="gap-2">
                        <Download className="h-4 w-4" />
                        Export Card
                    </Button>
                </div>
            </div>

            {/* The exportable square card */}
            <div className="flex justify-center">
                <div
                    ref={cardRef}
                    style={{ width: 600, height: 600 }}
                    className="flex flex-col p-6 bg-gradient-to-br from-[#0a1628] via-[#0f1d35] to-[#0a1628] rounded-xl border border-[#1e3a5f]/50 overflow-hidden uppercase"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-[#1e3a5f]/50 pb-3">
                        <div>
                            <p className="text-[#00D4AA] text-xs font-bold tracking-wider uppercase">{comp?.name || 'Race for the World Cup'}</p>
                            <h2 className="text-2xl font-black text-white leading-tight" {...editableProps}>{round.name}</h2>
                        </div>
                        <img
                            src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png"
                            alt="Podium Sports"
                            className="h-8 object-contain"
                            crossOrigin="anonymous"
                        />
                    </div>

                    {/* Week stat chips */}
                    <div className="grid grid-cols-3 gap-2 mt-3">
                        {[
                            { val: round.total_predictions, label: 'Predictions', color: 'text-white' },
                            { val: round.exact_scores, label: 'Exact Scores', color: 'text-green-400' },
                            { val: round.correct_results, label: 'Correct', color: 'text-blue-400' },
                        ].map(s => (
                            <div key={s.label} className="bg-[#0f1d35]/80 rounded-lg py-2 text-center border border-[#1e3a5f]/50">
                                <p className={`text-xl font-black ${s.color} tabular-nums`}>{s.val}</p>
                                <p className="text-[10px] text-slate-400">{s.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Top 3 — clean ranked list */}
                    {top3.length >= 1 && (
                        <div className="mt-4 space-y-2">
                            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">Round Leaderboard</p>
                            {top3.map((row, i) => (
                                // Full table-cell layout — html2canvas mis-renders flex
                                // `align-items: center`, so every column (and the digit inside
                                // the circle) is centred via table-cell + vertical-align:middle.
                                <div key={row.user_id || i} className="bg-[#0f1d35]/60 rounded-xl px-4" style={{ display: 'table', width: '100%', height: 52 }}>
                                    <span style={{ display: 'table-cell', verticalAlign: 'middle', width: 24 }}>
                                        <span className={`rounded-full text-xs font-black ${rankStyles[i]}`} style={{ display: 'table', width: 24, height: 24 }}>
                                            <span style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'center' }}>{i + 1}</span>
                                        </span>
                                    </span>
                                    <span className="text-white font-bold text-lg whitespace-nowrap" style={{ display: 'table-cell', verticalAlign: 'middle', paddingLeft: 12 }} {...editableProps}>{row.username}</span>
                                    <span className="text-[#00D4AA] font-black tabular-nums text-lg whitespace-nowrap" style={{ display: 'table-cell', verticalAlign: 'middle', textAlign: 'right' }}>{row.points} pts</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Fun facts — row of 3 stat tablets */}
                    <div className="flex-1 flex flex-col justify-end mt-4">
                        <div className="grid grid-cols-3 gap-2">
                            {facts.map((f, i) => <FactTile key={i} fact={f} editableProps={editableProps} badgeData={badgeData} />)}
                        </div>
                    </div>

                    {/* Footer */}
                    <p className="text-center text-slate-500 text-[11px] mt-3 tracking-wide">racewise.app · #RaceForTheWorldCup</p>
                </div>
            </div>
        </div>
    );
}

function TournamentSummaryCard({ data }) {
    const summaryRef = useRef(null);
    const [editMode, setEditMode] = useState(false);
    const summary = data.tournament_summary || {};
    const comp = data.competition || {};

    const handleExport = async () => {
        if (!summaryRef.current) return;
        setEditMode(false);
        await new Promise(r => setTimeout(r, 120));
        try {
            const canvas = await html2canvas(summaryRef.current, {
                backgroundColor: '#0a1628',
                scale: 2,
                useCORS: true,
            });
            const link = document.createElement('a');
            link.download = `${comp.name || 'football-tournament'}-summary.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            toast.success('Summary card exported!');
        } catch {
            toast.error('Failed to export image');
        }
    };

    const editableProps = editMode ? {
        contentEditable: true,
        suppressContentEditableWarning: true,
        style: { outline: '1px dashed rgba(0,212,170,0.5)', borderRadius: '4px', padding: '2px 4px', cursor: 'text' },
    } : {};

    return (
        <div className="space-y-4">
            <div className="flex justify-end gap-2">
                <Button variant={editMode ? 'default' : 'outline'} onClick={() => setEditMode(!editMode)} className="gap-2">
                    <Pencil className="h-4 w-4" />
                    {editMode ? 'Editing' : 'Edit Text'}
                </Button>
                <Button variant="outline" onClick={handleExport} className="gap-2">
                    <Download className="h-4 w-4" />
                    Export Card
                </Button>
            </div>

            {/* The exportable card */}
            <div
                ref={summaryRef}
                className="space-y-6 p-6 bg-gradient-to-br from-[#0a1628] via-[#0f1d35] to-[#0a1628] rounded-xl border border-[#1e3a5f]/50"
            >
                {/* Header */}
                <div className="text-center border-b border-[#1e3a5f]/50 pb-6">
                    <div className="flex justify-center mb-4">
                        <img
                            src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png"
                            alt="Podium Sports"
                            className="h-12 object-contain"
                        />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-1" {...editableProps}>
                        {comp.name || 'Tournament'}
                    </h2>
                    <p className="text-[#00D4AA] text-base font-semibold" {...editableProps}>
                        RACE FOR THE WORLD CUP
                    </p>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { emoji: '👥', val: data.total_entries, label: 'Entrants' },
                        { emoji: '⚽', val: summary.total_matches || 0, label: 'Matches' },
                        { emoji: '🎯', val: data.total_exact_scores, label: 'Exact Scores' },
                        { emoji: '✅', val: data.total_correct_results, label: 'Correct Results' },
                    ].map(s => (
                        <div key={s.label} className="bg-[#0f1d35]/80 rounded-xl p-4 text-center border border-[#1e3a5f]/50">
                            <span className="text-2xl block mb-1">{s.emoji}</span>
                            <p className="text-3xl font-black text-white">{s.val}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
                        </div>
                    ))}
                </div>

                {/* Tournament Champion */}
                {summary.overall_winner && (
                    <div className="bg-gradient-to-r from-[#1e3a5f]/40 via-[#00D4AA]/10 to-[#1e3a5f]/40 rounded-xl p-6 border border-[#00D4AA]/40 text-center">
                        <h3 className="text-[#00D4AA] font-bold text-base mb-3 flex items-center justify-center gap-2">
                            <span>👑</span> TOURNAMENT CHAMPION <span>👑</span>
                        </h3>
                        <p className="text-4xl font-black text-white mb-1" {...editableProps}>
                            {summary.overall_winner.username}
                        </p>
                        <p className="text-2xl text-[#00D4AA] font-bold tabular-nums" {...editableProps}>
                            {summary.overall_winner.total_points} points
                        </p>
                    </div>
                )}

                {/* Final Podium */}
                {summary.overall_top3?.length >= 2 && (
                    <div>
                        <h3 className="text-center text-slate-400 font-semibold mb-6 tracking-wider text-sm">FINAL PODIUM</h3>
                        <div className="flex justify-center items-end gap-3 md:gap-6">
                            {/* 2nd */}
                            <div className="flex flex-col items-center">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center shadow-lg mb-3">
                                    <span className="text-2xl font-black text-white">2</span>
                                </div>
                                <div className="h-24 w-24 md:w-28 bg-gradient-to-t from-slate-500 to-slate-400 rounded-t-xl flex flex-col items-center justify-end pb-3">
                                    <span className="text-xl mb-1">🥈</span>
                                    <span className="text-white font-bold text-sm">2nd</span>
                                </div>
                                <div className="bg-[#0f1d35] w-24 md:w-28 p-3 rounded-b-xl border-x border-b border-[#1e3a5f]/50 text-center">
                                    <p className="font-bold text-white text-sm" {...editableProps}>{summary.overall_top3[1]?.username}</p>
                                    <p className="text-slate-400 text-xs mt-0.5" {...editableProps}>{summary.overall_top3[1]?.total_points} pts</p>
                                </div>
                            </div>

                            {/* 1st */}
                            <div className="flex flex-col items-center -mt-6">
                                <span className="text-3xl mb-2">👑</span>
                                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[#00D4AA] to-[#00a88a] flex items-center justify-center shadow-xl shadow-[#00D4AA]/30 mb-3 ring-4 ring-[#00D4AA]/40">
                                    <span className="text-3xl font-black text-white">1</span>
                                </div>
                                <div className="h-32 w-28 md:w-36 bg-gradient-to-t from-[#00a88a] to-[#00D4AA] rounded-t-xl flex flex-col items-center justify-end pb-3">
                                    <span className="text-3xl mb-1">🏆</span>
                                    <span className="text-white font-bold">1st</span>
                                </div>
                                <div className="bg-[#0f1d35] w-28 md:w-36 p-4 rounded-b-xl border-x border-b border-[#00D4AA]/40 text-center">
                                    <p className="font-bold text-white" {...editableProps}>{summary.overall_top3[0]?.username}</p>
                                    <p className="text-[#00D4AA] font-bold tabular-nums text-lg mt-0.5" {...editableProps}>{summary.overall_top3[0]?.total_points} pts</p>
                                </div>
                            </div>

                            {/* 3rd */}
                            {summary.overall_top3[2] && (
                                <div className="flex flex-col items-center">
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00a88a] to-[#1e3a5f] flex items-center justify-center shadow-lg mb-3">
                                        <span className="text-2xl font-black text-white">3</span>
                                    </div>
                                    <div className="h-20 w-24 md:w-28 bg-gradient-to-t from-[#1e3a5f] to-[#00a88a] rounded-t-xl flex flex-col items-center justify-end pb-3">
                                        <span className="text-xl mb-1">🥉</span>
                                        <span className="text-white font-bold text-sm">3rd</span>
                                    </div>
                                    <div className="bg-[#0f1d35] w-24 md:w-28 p-3 rounded-b-xl border-x border-b border-[#1e3a5f]/50 text-center">
                                        <p className="font-bold text-white text-sm" {...editableProps}>{summary.overall_top3[2]?.username}</p>
                                        <p className="text-[#00a88a] text-xs mt-0.5" {...editableProps}>{summary.overall_top3[2]?.total_points} pts</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Fun Facts */}
                {summary.fun_facts?.length > 0 && (
                    <div>
                        <h3 className="text-center text-[#00D4AA] font-semibold mb-4 tracking-wider text-sm">TOURNAMENT HIGHLIGHTS</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {summary.fun_facts.map((fact, idx) => (
                                <div key={idx} className="bg-gradient-to-br from-[#0f1d35] to-[#0a1628] rounded-xl p-4 border border-[#1e3a5f]/50">
                                    <p className="text-base mb-2">{fact.title}</p>
                                    {fact.type === 'most_exact_scores' && (
                                        <div>
                                            <p className="text-white font-bold text-lg" {...editableProps}>{fact.user_name}</p>
                                            <p className="text-green-400 font-bold text-2xl tabular-nums">{fact.count} exact scores</p>
                                        </div>
                                    )}
                                    {fact.type === 'most_correct_results' && (
                                        <div>
                                            <p className="text-white font-bold text-lg" {...editableProps}>{fact.user_name}</p>
                                            <p className="text-blue-400 font-bold text-2xl tabular-nums">{fact.count} correct</p>
                                            <p className="text-slate-400 text-sm">from {fact.total_picks} picks</p>
                                        </div>
                                    )}
                                    {fact.type === 'highest_scoring_pred' && (
                                        <div>
                                            <p className="text-white font-bold text-lg" {...editableProps}>{fact.user_name}</p>
                                            <p className="text-[#00D4AA] font-black text-2xl tabular-nums">{fact.predicted_score}</p>
                                            <p className="text-slate-400 text-sm">{fact.match}</p>
                                            <p className="text-amber-400 text-sm font-semibold">+{fact.points} pts</p>
                                        </div>
                                    )}
                                    {fact.type === 'popular_correct' && (
                                        <div>
                                            <p className="text-white font-bold text-lg" {...editableProps}>{fact.match}</p>
                                            <p className="text-green-400 font-black text-2xl tabular-nums">{fact.score}</p>
                                            <p className="text-slate-400 text-sm">{fact.count} people predicted correctly ({fact.percentage}%)</p>
                                        </div>
                                    )}
                                    {fact.type === 'no_one_correct' && (
                                        <div>
                                            <p className="text-white font-bold text-lg" {...editableProps}>{fact.match}</p>
                                            <p className="text-red-400 font-black text-2xl tabular-nums">{fact.actual_score}</p>
                                            <p className="text-slate-400 text-sm">0 of {fact.total_pickers} predicted this</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Round Winners */}
                {summary.round_winners?.length > 0 && (
                    <div>
                        <h3 className="text-center text-[#00D4AA] font-semibold mb-4 tracking-wider text-sm">ROUND CHAMPIONS</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {summary.round_winners.map((rw, idx) => (
                                <div key={idx} className="bg-[#0f1d35]/80 rounded-xl p-4 border border-[#1e3a5f]/50 text-center">
                                    <p className="text-xs text-[#00D4AA] font-semibold mb-2">{rw.round_name}</p>
                                    <Star className="h-5 w-5 text-amber-400 mx-auto mb-2" />
                                    <p className="font-bold text-white" {...editableProps}>{rw.user_name}</p>
                                    <p className="text-xs text-slate-400 mt-1">{rw.points} pts</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="text-center pt-4 border-t border-[#1e3a5f]/50">
                    <p className="text-slate-500 text-xs mb-2">Powered by</p>
                    <img
                        src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png"
                        alt="Podium Sports"
                        className="h-5 mx-auto object-contain opacity-60"
                    />
                </div>
            </div>
        </div>
    );
}
